"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * Clamp long content to a preview height, with a "see more" toggle.
 *
 * The class names are load-bearing: `.react-expand-collapse__*` in `globals.css`
 * draws the fade-out gradient over the clamped text and positions the toggle, and
 * those rules were ported alongside the component.
 *
 * Two differences from the legacy class component:
 *
 *  - It re-measures. Legacy measured once in `componentDidMount`, so a
 *    description containing an image that hadn't loaded yet was measured at the
 *    wrong height and either kept a toggle it didn't need or lost one it did.
 *  - The toggle is a real `<button>`. Legacy put `role="button"` on a div and then
 *    nested an actual `<button>` inside it, which is invalid and reaches the
 *    keyboard twice. The CSS targets both the wrapper and the button, so the
 *    styling is unchanged.
 */
export function ExpandCollapse({
  children,
  previewHeight,
  expandLabel,
  collapseLabel,
  expandOnBodyClick = false,
  className,
}: {
  children: ReactNode;
  /** Height in px the content is clamped to while collapsed. */
  previewHeight: number;
  expandLabel: ReactNode;
  collapseLabel: ReactNode;
  /** Clicking the text itself expands it, as well as the toggle. */
  expandOnBodyClick?: boolean;
  className?: string;
}) {
  const [isExpanded, setExpanded] = useState(false);
  // Starts true so the very first paint is the clamped one: assuming the content
  // fits and then clamping it would show the whole description for a frame.
  const [canExpand, setCanExpand] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const content = contentRef.current;
    const body = bodyRef.current;
    if (!content || !body) return;

    const measure = () => {
      // Only meaningful while clamped — expanded, the box is `height: auto` and
      // the two heights are equal by construction, which would always read as
      // "nothing to expand".
      if (isExpanded) return;
      setCanExpand(
        body.getBoundingClientRect().height >=
          content.getBoundingClientRect().height + 2
      );
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, [isExpanded, children]);

  return (
    <div>
      <div
        ref={contentRef}
        className={cn(
          "react-expand-collapse__content",
          isExpanded && "react-expand-collapse--expanded",
          className
        )}
        style={{ height: isExpanded || !canExpand ? "auto" : previewHeight - 2 }}
      >
        <div
          ref={bodyRef}
          className="react-expand-collapse__body text-sm"
          onClick={
            expandOnBodyClick && canExpand && !isExpanded
              ? () => setExpanded(true)
              : undefined
          }
        >
          {children}
        </div>
      </div>

      {canExpand && (
        <div className="react-expand-collapse__button">
          <button
            type="button"
            className="p-0 text-right text-sm"
            aria-expanded={isExpanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {isExpanded ? collapseLabel : expandLabel}
          </button>
        </div>
      )}
    </div>
  );
}
